/**
 * CatalogShelf — one horizontal shelf in a Discover pillar catalog (DEV-1447).
 *
 * Scroll affordances: prev/next arrows and an edge fade, both of which appear
 * ONLY when the shelf actually overflows. A "See all" action hands the shelf's
 * full (unsliced) list to the grid below.
 *
 * RTL: `scrollBy({ left })` is direction-aware in every modern engine (positive
 * = "forward in reading order"), so the arrows are mirrored automatically. The
 * overflow probe uses inline-size, not width.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from './CommunitiesCatalogPage.module.css';

interface Props {
  title: string;
  /** Shown when the shelf's slice is already applied to the grid. */
  onSeeAll?: () => void;
  /** Total size of the slice, so "See all" can say what it will show. */
  totalCount?: number;
  /**
   * The LEAD shelf — wider columns for the large cards. Positional: the first
   * shelf that renders is the lead, whichever one that happens to be.
   */
  lead?: boolean;
  children: React.ReactNode;
}

const CatalogShelf: React.FC<Props> = ({ title, onSeeAll, totalCount, lead = false, children }) => {
  const { t } = useAppTranslation('discover');
  const rowRef = useRef<HTMLDivElement>(null);

  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollFwd, setCanScrollFwd] = useState(false);

  /** Probe the scroll position. `scrollLeft` is negative in RTL, so compare on
   *  magnitude rather than sign. */
  const probe = useCallback(() => {
    const el = rowRef.current;
    if (!el) return;
    const pos = Math.abs(el.scrollLeft);
    const max = el.scrollWidth - el.clientWidth;
    setCanScrollBack(pos > 4);
    setCanScrollFwd(max > 4 && pos < max - 4);
  }, []);

  useEffect(() => {
    probe();
    const el = rowRef.current;
    if (!el) return;
    // Re-probe when the shelf resizes (window resize, rail collapse, font load).
    const ro = new ResizeObserver(probe);
    ro.observe(el);
    return () => ro.disconnect();
  }, [probe, children]);

  const scrollByPage = (dir: 1 | -1) => {
    const el = rowRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  const overflows = canScrollBack || canScrollFwd;

  return (
    <section className={`${styles.shelf} ${lead ? styles.shelfLead : ''}`}>
      <div className={styles.shelfHead}>
        <h2 className={`${styles.shelfTitle} ${lead ? styles.shelfTitleLead : ''}`}>{title}</h2>

        {onSeeAll && (
          <button type="button" className={styles.seeAll} onClick={onSeeAll}>
            {totalCount !== undefined
              ? t('catalog.seeAllCount', { count: totalCount })
              : t('catalog.seeAll')}
          </button>
        )}

        {/* Arrows only exist when there is something to scroll to. A disabled
            arrow on a shelf that fits would be chrome pretending to be a
            control. */}
        {overflows && (
          <div className={styles.shelfNav}>
            <button
              type="button"
              className={styles.shelfArrow}
              onClick={() => scrollByPage(-1)}
              disabled={!canScrollBack}
              aria-label={t('catalog.scrollBack')}
            >
              <span aria-hidden="true">‹</span>
            </button>
            <button
              type="button"
              className={styles.shelfArrow}
              onClick={() => scrollByPage(1)}
              disabled={!canScrollFwd}
              aria-label={t('catalog.scrollForward')}
            >
              <span aria-hidden="true">›</span>
            </button>
          </div>
        )}
      </div>

      <div className={styles.shelfViewport}>
        <div
          ref={rowRef}
          className={`${styles.shelfRow} ${lead ? styles.shelfRowLead : ''}`}
          onScroll={probe}
        >
          {children}
        </div>
        {/* Edge fades — pure affordance, never intercept pointer events. */}
        {canScrollBack && <div className={`${styles.fade} ${styles.fadeStart}`} aria-hidden="true" />}
        {canScrollFwd && <div className={`${styles.fade} ${styles.fadeEnd}`} aria-hidden="true" />}
      </div>
    </section>
  );
};

export default CatalogShelf;
